import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Box,
  Text,
  Avatar,
  HStack,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import { useState, useEffect } from 'react';
import { User } from '../../types';
import { useAuth } from '../../../../contexts/AuthContext';

interface AddMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMembersAdded: (conversationId: string) => void;
  conversationId: string;
  currentMemberIds: string[];
  addMembersToConversation: (conversationId: string, memberIds: string[]) => Promise<string>;
}

export function AddMembersModal({
  isOpen,
  onClose,
  onMembersAdded,
  conversationId,
  currentMemberIds,
  addMembersToConversation,
}: AddMembersModalProps) {
  const toast = useToast();
  const { currentUser, eosUsers = [] } = useAuth();
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);

  // Filter EOS users to exclude current user and existing members
  useEffect(() => {
    const filtered = eosUsers.filter(
      (user) =>
        user.id !== currentUser?.uid &&
        user.id !== currentUser?.id &&
        !currentMemberIds.includes(user.id)
    );
    setFilteredUsers(filtered);
  }, [eosUsers, currentUser, currentMemberIds]);

  // Reset selections when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedMemberIds([]);
    }
  }, [isOpen]);

  const handleSearch = (searchQuery: string) => {
    if (!searchQuery.trim()) {
      const filtered = eosUsers.filter(
        (user) =>
          user.id !== currentUser?.uid &&
          user.id !== currentUser?.id &&
          !currentMemberIds.includes(user.id)
      );
      setFilteredUsers(filtered);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = eosUsers.filter(
      (user) =>
        (user.name?.toLowerCase().includes(query) ||
          user.Name?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          user.Email?.toLowerCase().includes(query)) &&
        user.id !== currentUser?.uid &&
        user.id !== currentUser?.id &&
        !currentMemberIds.includes(user.id)
    );

    setFilteredUsers(filtered);
  };

  const toggleMemberSelection = (userId: string) => {
    setSelectedMemberIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAddMembers = async () => {
    if (selectedMemberIds.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one member to add',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const resultConversationId = await addMembersToConversation(
        conversationId,
        selectedMemberIds
      );
      
      // If a different conversation ID is returned, it means we found a duplicate
      if (resultConversationId !== conversationId) {
        toast({
          title: 'Conversation found',
          description: 'A conversation with these members already exists',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Members added',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
      
      onMembersAdded(resultConversationId);
      onClose();
      setSelectedMemberIds([]);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add members',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Members</ModalHeader>
        <ModalCloseButton />
        <Formik
          initialValues={{ searchQuery: '' }}
          onSubmit={() => {}}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <Field name="searchQuery">
                    {({ field }: any) => (
                      <FormControl>
                        <FormLabel>Search for users to add</FormLabel>
                        <Input
                          {...field}
                          placeholder="Type a name or email..."
                          onChange={(e) => {
                            setFieldValue('searchQuery', e.target.value);
                            handleSearch(e.target.value);
                          }}
                        />
                      </FormControl>
                    )}
                  </Field>

                  {selectedMemberIds.length > 0 && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Selected: {selectedMemberIds.length} member(s)
                      </Text>
                    </Box>
                  )}

                  <Box
                    maxH="300px"
                    overflowY="auto"
                    borderWidth="1px"
                    borderRadius="md"
                    p={2}
                  >
                    {filteredUsers.length === 0 ? (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                        No users found
                      </Text>
                    ) : (
                      <VStack spacing={2} align="stretch">
                        {filteredUsers.map((user) => {
                          const userName = user.name || user.Name || 'Unknown';
                          const userEmail = user.email || user.Email || '';
                          const isSelected = selectedMemberIds.includes(user.id);

                          return (
                            <Box
                              key={user.id}
                              p={2}
                              borderRadius="md"
                              bg={isSelected ? 'blue.50' : 'transparent'}
                              borderWidth={isSelected ? '2px' : '1px'}
                              borderColor={isSelected ? 'blue.500' : 'gray.200'}
                              cursor="pointer"
                              _hover={{ bg: 'gray.50' }}
                              onClick={() => toggleMemberSelection(user.id)}
                            >
                              <HStack spacing={3}>
                                <Avatar
                                  size="sm"
                                  src={user.photo || user.photoURL}
                                  name={userName}
                                />
                                <VStack align="flex-start" spacing={0} flex="1">
                                  <Text fontSize="sm" fontWeight="medium">
                                    {userName}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {userEmail}
                                  </Text>
                                </VStack>
                              </HStack>
                            </Box>
                          );
                        })}
                      </VStack>
                    )}
                  </Box>
                </VStack>
              </ModalBody>

              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleAddMembers}
                  isDisabled={selectedMemberIds.length === 0}
                >
                  Add Members
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}

